// Beat alignment for the metronome-as-master-timebase model. The click never re-phases;
// instead the playhead is held until starting it puts the song's beats *on* the click.
// Both advance at dt × tempo, so aligning the next beat once aligns every beat after it
// (on a uniform grid). Pure leaves — the caller owns the clock.

/**
 * Music-seconds to hold the playhead so the song's next beat lands on a metronome click.
 * `timeToNextClick` (φ) and `timeToNextBeat` (g) are both music-seconds from now; after a
 * hold of `d` the beat arrives at `d + g`, and we want that ≡ φ (mod period). Always in
 * [0, period) — the wait is never a whole beat or more. Pure.
 */
export function alignDelay(
  timeToNextClick: number,
  timeToNextBeat: number,
  period: number,
): number {
  if (period <= 0) return 0;
  const d = (timeToNextClick - timeToNextBeat) % period;
  return (d + period) % period; // JS % keeps the sign of the dividend
}

/**
 * Music-seconds from `playhead` to the song's next beat — the next beat that will actually
 * be *clicked*, so a playhead sitting exactly on a beat measures to the following one
 * (`beatsDue` is half-open on `prev`, so the beat under the playhead never re-fires).
 * Extrapolates on `period` past the end of the grid (and when there is no grid). Pure.
 */
export function nextBeatIn(playhead: number, beats: readonly number[], period: number): number {
  if (period <= 0) return 0;
  // beats is ascending — binary-search the first beat strictly after the playhead.
  let lo = 0;
  let hi = beats.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((beats[mid] ?? 0) <= playhead) lo = mid + 1;
    else hi = mid;
  }
  const next = beats[lo];
  if (next !== undefined) return next - playhead;
  const last = beats[beats.length - 1];
  if (last === undefined) return period; // no grid — the pulse's own period is all we have
  const k = Math.floor((playhead - last) / period) + 1;
  return last + k * period - playhead;
}
